import React from 'react';
import { Link } from 'react-router-dom';

const Home = () => {
  return (
    <div>
      {/* Hero Section - Educational Institution Style */}
      <section className="relative overflow-hidden min-h-screen -mt-20">
        {/* Background */}
        <div className="absolute inset-0 z-0" style={{ top: '-80px', height: 'calc(100% + 80px)' }}>
          <video 
            src="/assets/hero-video-compressed.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-40"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-32 pb-16 sm:pb-32 min-h-screen flex items-center">
          <div className="text-center max-w-5xl mx-auto w-full">
            {/* Institution Name */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-light mb-4 sm:mb-6 leading-[1.1] text-white tracking-tight">
              <span className="block sm:inline">Sonom Performance</span>
              <span className="block">Institute</span>
            </h1>
            
            {/* Tagline */}
            <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-gray-100 mb-6 sm:mb-8 font-light tracking-wide px-2">
              <span className="block sm:inline">Unlocking Potential</span>
              <span className="hidden sm:inline mx-2">|</span>
              <span className="block sm:inline">Elevating Performance</span>
              <span className="hidden sm:inline mx-2">|</span>
              <span className="block sm:inline">Building Resilience</span>
            </p>
            
            
            {/* Primary Actions */}
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center px-4">
              <Link 
                to="/research-2026"
                className="bg-white text-spi-700 hover:bg-gray-100 px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold text-base sm:text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-center"
              >
                <span className="block sm:inline">Learn More</span>
              </Link>
              <Link 
                to="/advisory-board"
                className="border-2 border-white text-white hover:bg-white hover:text-spi-700 px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold text-base sm:text-lg transition-all duration-300 transform hover:scale-105 text-center"
              >
                <span className="block sm:inline">Meet Our Advisory Board</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Core Programs - Educational Layout */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-black mb-4 sm:mb-6 tracking-tight px-2">
              Four Pillars of Excellence
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
            {/* Cyber Schooling */}
            <div className="group">
              <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 h-full hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-100 rounded-xl flex items-center justify-center mb-4 sm:mb-6">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C20.832 18.477 19.246 18 17.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-black mb-3 sm:mb-4">Cyber Schooling</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-4">
                  Flexible, high-standard coursework
                </p>
                <a
                  href="https://youtu.be/5fC2vOf7B6k?si=KVwybaZvZOtzn0Ep"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                  Watch Video
                </a>
              </div>
            </div>

            {/* Performance Training */}
            <div className="group">
              <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 h-full hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-red-100 rounded-xl flex items-center justify-center mb-4 sm:mb-6">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-black mb-3 sm:mb-4">Performance Training</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  High-performance athlete development
                </p>
              </div>
            </div>

            {/* Mental Health */}
            <div className="group">
              <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 h-full hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 rounded-xl flex items-center justify-center mb-4 sm:mb-6">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-black mb-3 sm:mb-4">Mental Health</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  Second Step: Comprehensive mental health support
                </p>
              </div>
            </div>

            {/* NIL U */}
            <div className="group">
              <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 h-full hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-purple-100 rounded-xl flex items-center justify-center mb-4 sm:mb-6">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-black mb-3 sm:mb-4">NIL U</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  Preparing athletes for tomorrow
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Upcoming Program - Call to Action */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-black mb-4 sm:mb-6 tracking-tight px-2">
              Join Our Pioneer Class
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed px-4">
              Groundbreaking student-athlete experience
            </p>
          </div>

          {/* Program Preview */}
          <div className="bg-gradient-to-r from-spi-600 to-spi-700 rounded-2xl p-6 sm:p-8 lg:p-12 text-white text-center mb-8 sm:mb-12">
            <h3 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">August 2026 - Research and Development Launch</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 mb-6 sm:mb-8">
              <div>
                <div className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">4</div>
                <div className="text-spi-200 text-xs sm:text-sm lg:text-base">Month Duration</div>
              </div>
              <div>
                <div className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">2</div>
                <div className="text-spi-200 text-xs sm:text-sm lg:text-base">Student-Athletes</div>
              </div>
              <div>
                <div className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">Aug 2026</div>
                <div className="text-spi-200 text-xs sm:text-sm lg:text-base">Launch</div>
              </div>
              <div>
                <div className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">$0</div>
                <div className="text-spi-200 text-xs sm:text-sm lg:text-base">Cost to Families</div>
              </div>
            </div>
            <Link 
              to="/research-2026"
              className="inline-block bg-white text-spi-700 px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold text-base sm:text-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-105"
            >
              <span className="block sm:inline">Learn More</span>
            </Link>
          </div>

          {/* Dynamic Partnerships */}
          <div className="mb-16">
            <h3 className="text-2xl sm:text-3xl font-light text-center mb-8 text-gray-900">Dynamic Partnerships</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="rounded-xl overflow-hidden shadow-lg">
                <div className="h-32 bg-white flex items-center justify-center p-4">
                  <img 
                    src="/assets/trout-cpa-logo.svg"
                    alt="Trout CPA Logo"
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <div className="p-4 bg-white">
                  <h4 className="font-semibold text-gray-900 mb-1">Trout CPA</h4>
                  <p className="text-sm text-gray-600">Financial guidance through NIL simulation</p>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden shadow-lg">
                <div className="h-32 bg-white flex items-center justify-center p-4">
                  <img 
                    src="/assets/l-i-g-sports-group_0_7510 (1).png"
                    alt="L.I.G. Sports Group Logo"
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <div className="p-4 bg-white">
                  <h4 className="font-semibold text-gray-900 mb-1">Justin King</h4>
                  <p className="text-sm text-gray-600 mb-2">Advanced speed training support</p>
                  <a
                    href="https://www.youtube.com/watch?v=xCkIYZM9D7w"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-spi-600 hover:text-spi-700 font-medium text-xs transition-colors"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                    Watch Video
                  </a>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden shadow-lg">
                <div className="h-32 bg-white flex items-center justify-center p-4">
                  <img 
                    src="/assets/Team.Beast.Mode.png"
                    alt="Team Beast Mode Logo"
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <div className="p-4 bg-white">
                  <h4 className="font-semibold text-gray-900 mb-1">Team Beast Mode</h4>
                  <p className="text-sm text-gray-600 mb-2">Host facility; personal training with an impact</p>
                  <a
                    href="https://youtu.be/jZTqhRjWvvo?si=Dp50OuGhyaIV2Yfl"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-spi-600 hover:text-spi-700 font-medium text-xs transition-colors"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                    Watch Video
                  </a>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden shadow-lg">
                <div className="h-32 bg-blue-600 flex items-center justify-center p-4">
                  <span className="text-white font-bold text-xl">LinkedIn</span>
                </div>
                <div className="p-4 bg-white">
                  <h4 className="font-semibold text-gray-900 mb-1">Michael Susi</h4>
                  <p className="text-sm text-gray-600">Maximizing recovery for success</p>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden shadow-lg">
                <div className="h-32 bg-white flex items-center justify-center p-4">
                  <img 
                    src="/assets/RSFA-2.png"
                    alt="RSFA Logo"
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <div className="p-4 bg-white">
                  <h4 className="font-semibold text-gray-900 mb-1">Ron Johnson</h4>
                  <p className="text-sm text-gray-600 mb-2">Resilience to navigate life</p>
                  <a
                    href="https://youtu.be/VBXg0LsnCOc?si=5rQKrw1MoHX5"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-spi-600 hover:text-spi-700 font-medium text-xs transition-colors"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                    Watch Video
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Testimonials */}
          <div className="mb-16">
            <h3 className="text-2xl sm:text-3xl font-light text-center mb-10 text-gray-900">What People Are Saying</h3>

            {/* Professional Testimonial */}
            <div className="bg-spi-50 rounded-2xl p-6 sm:p-8 border border-spi-100 mb-8">
              <svg className="w-8 h-8 text-spi-300 mb-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
              </svg>
              <p className="text-gray-700 leading-relaxed mb-6 text-sm sm:text-base">
                Working with Todd was both energizing and joyful. He asked thoughtful questions and listened attentively. His feedback was timely and constructive and backed by experience and expertise. It was evident that he was motivated to help us improve and that makes him a pleasure to work with.
              </p>
              <div>
                <p className="font-semibold text-gray-900">Nina Maldonado</p>
                <p className="text-sm text-gray-500">Youth Worker/Girls Program Coordinator, Water Street Mission, Lancaster PA</p>
              </div>
            </div>

            {/* Student Testimonials */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 border border-gray-100">
                <svg className="w-8 h-8 text-spi-300 mb-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                </svg>
                <p className="text-gray-700 leading-relaxed mb-6 text-sm sm:text-base">
                  Dr. Monos is a great person to be around, he's helped me through my toughest times. He helped me through my family issues, losing friends, and feeling alone. He helped me feel seen and heard, he played the biggest part in me graduating and getting my life together. I was on probation, going down the wrong path, I had no one and he was there to uplift me, help me see my values and helped me grow as an individual.
                </p>
                <div>
                  <p className="font-semibold text-gray-900">Lexi H.</p>
                  <p className="text-sm text-gray-500">Former Student</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 border border-gray-100">
                <svg className="w-8 h-8 text-spi-300 mb-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                </svg>
                <p className="text-gray-700 leading-relaxed mb-6 text-sm sm:text-base">
                  As a student that transferred during Covid, it made it more difficult to get a solid footing. With Dr. Monos getting incorporated into my schedule, he taught me how to use the struggles of going through school to work for me and not against me. He showed a patient mindset with a lenient work ethic to give me and other students hope and eventually the help and mindset that we needed.
                </p>
                <div>
                  <p className="font-semibold text-gray-900">Nyko S.</p>
                  <p className="text-sm text-gray-500">Former Student</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="text-center px-4">
            <p className="text-gray-600 mb-6 text-sm sm:text-base">
              Ready to unlock your potential? Contact us to learn more about enrollment.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center max-w-md sm:max-w-none mx-auto">
              <a 
                href="mailto:sonom.advantage@gmail.com"
                className="bg-black hover:bg-gray-800 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-medium transition-colors duration-200 text-sm sm:text-base text-center"
              >
                <span className="block sm:inline">Contact Our Admissions Team</span>
              </a>
              <a 
                href="tel:717-577-4120"
                className="border border-black text-black hover:bg-black hover:text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-medium transition-colors duration-200 text-sm sm:text-base text-center"
              >
                Call (717) 577-4120
              </a>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;